import { useUpdateGroupMutation } from '@/app/redux/endpoints/groups';
import { useGroups } from '@/app/redux/hooks/groups';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import { Student } from '@/common/types/student';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import { Button } from '@shadcn/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/dialog';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { GroupMultiSelect } from '../MultiSelect';

interface ManageGroupsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  student: Student;
}
export default function ManageGroupsDialog({
  isOpen,
  onClose,
  student,
}: ManageGroupsDialogProps) {
  const { currentOrganisationId } = useCurrentOrganisationId();
  const { groups, groupsById } = useGroups();
  const [updateGroup] = useUpdateGroupMutation();
  const studentGroups = (groups || []).filter((group) =>
    group.studentIds.includes(student.id),
  );

  const [updatedGroupIds, setUpdatedGroupIds] = useState<string[]>(
    studentGroups.map((g) => g.id!),
  );
  const [isLoading, setLoading] = useState(false);

  const hasChanged = useMemo(() => {
    const studentGroupIds = studentGroups.map((g) => g.id!);
    if (studentGroupIds.length !== updatedGroupIds.length) {
      return true;
    }
    return (
      !studentGroupIds.every((id) => updatedGroupIds.includes(id)) ||
      !updatedGroupIds.every((id) => studentGroupIds.includes(id))
    );
  }, [studentGroups, updatedGroupIds]);

  async function handleGroupSelected(groupId: string) {
    setUpdatedGroupIds((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId],
    );
  }

  function handleFilterGroups(searchTerm: string) {
    const terms = searchTerm.split(' ');
    return (groups || []).filter((group) =>
      terms.every((term) =>
        group.name.toLowerCase().includes(term.toLowerCase()),
      ),
    );
  }

  async function handleUpdateGroups() {
    setLoading(true);
    try {
      // Add new groups
      for (const groupId of updatedGroupIds) {
        if (!studentGroups.find((group) => group.id === groupId)) {
          const g = groupsById.get(groupId);
          if (g) {
            await updateGroup({
              orgId: currentOrganisationId!,
              groupId,
              updates: { studentIds: [...g.studentIds, student.id] },
            }).unwrap();
          }
        }
      }

      // Remove students from old groups
      for (const groupId of studentGroups.map((group) => group.id!)) {
        if (!updatedGroupIds.includes(groupId)) {
          const g = groupsById.get(groupId);
          if (g) {
            await updateGroup({
              orgId: currentOrganisationId!,
              groupId,
              updates: { studentIds: g.studentIds.filter((id) => id !== student.id) },
            }).unwrap();
          }
        }
      }

      toast.success('Learner groups updated');
    } catch (error) {
      console.error('Failed to update learner groups:', error);
      toast.error('Failed to update learner groups', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    } finally {
      setLoading(false);
      onClose();
      setUpdatedGroupIds([]);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>Manage Groups</DialogTitle>
          <DialogDescription>
            Add or remove {student.studentFirstName} {student.studentSurname}{' '}
            from groups
          </DialogDescription>
        </DialogHeader>

        <GroupMultiSelect
          items={groups || []}
          selectedIds={updatedGroupIds}
          onSelect={handleGroupSelected}
          onFilter={handleFilterGroups}
          autoFocus
        />

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleUpdateGroups}
            loading={isLoading}
            disabled={!hasChanged}
          >
            Update
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
